// Event-driven ball physics: pure TypeScript, no DOM, three.js or Box2D.
//
// Balls are circles on a plane. The boundary is a set of line segments (the cushion line and the pocket jaws) and
// their end points (corner and jaw tips, point contacts). Pockets are convex polygons: a ball whose centre enters one
// is potted. Between events every ball moves under the damping law of physics/constants.ts, which in the damped
// clock s = (1 - e^(-k t)) / k is a straight line at constant velocity. So the solver:
//   1. finds the earliest next event, exactly: ball-ball contact (quadratic), ball-segment (linear),
//      ball-vertex (quadratic), pocket entry (convex clipping), and each ball's stop (closed form);
//   2. moves every ball analytically to that instant and resolves the event (impulse along the true contact
//      normal, restitution, stop, pot);
//   3. repeats. Simultaneous events are taken one after the other at the same instant.
// Sampling (position(), velocity()) evaluates the state at the current time without changing it, so the event
// sequence, and with it every final position, depends only on the initial state, never on how often it is sampled.
//
// Robustness: contacts are only taken while the pair approaches (a resolved pair separates, so the same contact can't
// re-fire), a pair found touching or overlapping by rounding while approaching is resolved at once (never let an
// overlap grow), tiny approach speeds are absorbed (MIN_BOUNCE_SPEED), and a per-shot event cap stops all balls
// in place rather than loop (MAX_EVENTS_PER_SHOT).
import {
    APPROACH_EPS, BALL_RESTITUTION, CUSHION_RESTITUTION, DAMPING, MAX_EVENTS_PER_SHOT, MIN_BOUNCE_SPEED, STOP_SPEED,
} from "./constants";

export type V2 = [number, number];

export interface Segment { a: V2; b: V2 }

export interface Boundary {
    segments: Segment[];
    /** segment end points that a ball can touch (corner and jaw tips) */
    vertices: V2[];
    /** convex polygons; a point is inside when it is on the inner side of every edge (same test as Math.ts) */
    pockets: V2[][];
}

export interface PhysicsParams {
    radius: number;
    damping: number;
    stopSpeed: number;
    ballRestitution: number;
    cushionRestitution: number;
    maxEventsPerShot: number;
}

export const DEFAULT_PARAMS: Omit<PhysicsParams, "radius"> = {
    damping: DAMPING,
    stopSpeed: STOP_SPEED,
    ballRestitution: BALL_RESTITUTION,
    cushionRestitution: CUSHION_RESTITUTION,
    maxEventsPerShot: MAX_EVENTS_PER_SHOT,
};

export type EventKind = "ball" | "segment" | "vertex" | "pocket" | "stop";

export interface SimListener {
    /** two balls touched (a struck b or the other way round); speed = normal approach speed */
    onBallContact?(a: string, b: string, speed: number): void;
    onCushion?(id: string, speed: number): void;
    /** the ball's centre entered a pocket; it is off the table from now on */
    onPotted?(id: string, p: V2): void;
    onStopped?(id: string): void;
    /** the per-shot event cap was hit: every ball was stopped where it was */
    onEventCap?(events: number): void;
}

interface SimBall {
    id: string;
    // state at baseTime
    x: number; y: number;
    vx: number; vy: number;
    onTable: boolean;
}

interface NextEvent { time: number; kind: EventKind; i: number; j: number }

/** Earliest s >= 0 (and <= limit) at which |dp + dv s| = dist while approaching, or Infinity. */
export function contactTime(dpx: number, dpy: number, dvx: number, dvy: number, dist: number, limit: number): number {
    const b = dpx * dvx + dpy * dvy;                 // half the derivative of |dp|^2
    const d0 = Math.hypot(dpx, dpy);
    if (!(b < -APPROACH_EPS * d0)) return Infinity;  // separating, tangential, or noise
    const c = dpx * dpx + dpy * dpy - dist * dist;
    if (c <= 0) return 0;                            // touching (or a rounding overlap) and approaching: now
    const a = dvx * dvx + dvy * dvy;
    const disc = b * b - a * c;
    if (disc < 0) return Infinity;                   // passes by
    const s = c / (-b + Math.sqrt(disc));            // the smaller root, in the cancellation-free form
    return s <= limit ? s : Infinity;
}

export class Simulation {
    readonly params: PhysicsParams;
    readonly boundary: Boundary;
    private balls: SimBall[] = [];
    private index = new Map<string, number>();
    /** time of the committed state (the last event or external change) */
    private baseTime = 0;
    /** the time position()/velocity() report (>= baseTime) */
    private now = 0;
    private next: NextEvent | null | undefined = undefined;   // undefined: not computed yet
    // Event caches, in absolute time. A ball's path only changes when an event or an external change touches it,
    // so after an event only the balls it touched are recomputed (their boundary events and their pairs).
    private allDirty = true;
    private dirty = new Set<number>();
    private ballT: number[] = [];
    private ballKind: EventKind[] = [];
    private ballJ: number[] = [];
    private pairT: number[][] = [];
    /** events since the last shot started (a ball was set moving while all were at rest) */
    eventsThisShot = 0;
    /** all events ever, and per kind (for tests and the report) */
    readonly counts: Record<EventKind, number> = {ball: 0, segment: 0, vertex: 0, pocket: 0, stop: 0};
    capHits = 0;
    /** optional observer for tests: called after every resolved event */
    onEvent?: (kind: EventKind, time: number) => void;
    private segData: {ax: number, ay: number, tx: number, ty: number, nx: number, ny: number, len: number}[];

    constructor(boundary: Boundary, params: Partial<PhysicsParams> & {radius: number}) {
        this.params = {...DEFAULT_PARAMS, ...params};
        this.boundary = boundary;
        this.segData = [];
        for (const seg of boundary.segments) {
            const dx = seg.b[0] - seg.a[0], dy = seg.b[1] - seg.a[1];
            const len = Math.hypot(dx, dy);
            if (len < 1e-12) continue;                    // zero-length edge: its end point is a vertex
            this.segData.push({ax: seg.a[0], ay: seg.a[1], tx: dx / len, ty: dy / len, nx: -dy / len, ny: dx / len, len});
        }
    }

    get time() { return this.now; }

    // ----- state -----

    has(id: string) { const i = this.index.get(id); return i !== undefined && this.balls[i].onTable; }

    ids(): string[] { return this.balls.filter(b => b.onTable).map(b => b.id); }

    /** Put a ball on the table (or move it) at rest. */
    place(id: string, p: V2) {
        this.commit();
        let i = this.index.get(id);
        if (i === undefined) {
            i = this.balls.length;
            this.balls.push({id, x: p[0], y: p[1], vx: 0, vy: 0, onTable: true});
            this.index.set(id, i);
        }
        const b = this.balls[i];
        b.x = p[0]; b.y = p[1]; b.vx = 0; b.vy = 0; b.onTable = true;
        this.touch(i);
    }

    /** Take a ball off the table; its last position stays readable. */
    remove(id: string) {
        const i = this.index.get(id);
        if (i === undefined) return;
        this.commit();
        const b = this.balls[i];
        b.vx = 0; b.vy = 0; b.onTable = false;
        this.touch(i);
    }

    /** Move a ball, keeping its velocity. */
    setPosition(id: string, p: V2) {
        const i = this.index.get(id);
        if (i === undefined) return this.place(id, p);
        this.commit();
        const b = this.balls[i];
        b.x = p[0]; b.y = p[1];
        this.touch(i);
    }

    /** Set a ball's velocity now (the cue strike). Starting a shot from rest resets the per-shot event count. */
    setVelocity(id: string, v: V2) {
        const i = this.index.get(id);
        if (i === undefined) return;
        this.commit();
        if (this.allStopped()) this.eventsThisShot = 0;
        const b = this.balls[i];
        if (!b.onTable) return;
        b.vx = v[0]; b.vy = v[1];
        this.touch(i);
    }

    position(id: string): V2 | null {
        const i = this.index.get(id);
        if (i === undefined) return null;
        const b = this.balls[i];
        if (!b.onTable || (b.vx === 0 && b.vy === 0)) return [b.x, b.y];
        const s = this.sAt(this.now - this.baseTime);
        return [b.x + b.vx * s, b.y + b.vy * s];
    }

    velocity(id: string): V2 {
        const i = this.index.get(id);
        if (i === undefined) return [0, 0];
        const b = this.balls[i];
        if (!b.onTable || (b.vx === 0 && b.vy === 0)) return [0, 0];
        const f = Math.exp(-this.params.damping * (this.now - this.baseTime));
        return [b.vx * f, b.vy * f];
    }

    positions(): Record<string, V2> {
        const out: Record<string, V2> = {};
        for (const b of this.balls) if (b.onTable) out[b.id] = this.position(b.id)!;
        return out;
    }

    allStopped() {
        for (const b of this.balls) if (b.onTable && (b.vx !== 0 || b.vy !== 0)) return false;
        return true;
    }

    /** Kinetic energy (unit masses) at the current time. */
    energy() {
        let e = 0;
        for (const b of this.balls) {
            if (!b.onTable) continue;
            const [vx, vy] = this.velocity(b.id);
            e += 0.5 * (vx * vx + vy * vy);
        }
        return e;
    }

    // ----- time -----

    /** Advance by dt seconds of simulated time, resolving every event on the way. */
    advance(dt: number, listener: SimListener = {}) {
        this.advanceTo(this.now + Math.max(0, dt), listener);
    }

    advanceTo(t: number, listener: SimListener = {}) {
        if (!(t >= this.now)) return;
        for (;;) {
            if (this.next === undefined) this.next = this.findNext();
            const ev = this.next;
            if (!ev) break;
            if (ev.time > t) break;
            const evTime = Math.max(ev.time, this.baseTime);
            this.rebase(this.sAt(evTime - this.baseTime), evTime);
            this.resolve(ev, listener);
            this.touch(ev.i);
            if (ev.kind === "ball") this.touch(ev.j);
            this.eventsThisShot++;
            this.counts[ev.kind]++;
            if (this.onEvent) this.onEvent(ev.kind, this.baseTime);
            if (this.eventsThisShot >= this.params.maxEventsPerShot && !this.allStopped()) {
                for (const b of this.balls) { b.vx = 0; b.vy = 0; }
                this.allDirty = true;
                this.next = undefined;
                this.capHits++;
                if (listener.onEventCap) listener.onEventCap(this.eventsThisShot);
                break;
            }
        }
        this.now = Math.max(this.now, t, this.baseTime);
    }

    /** Run until every ball has stopped (or maxTime). Returns the simulated duration. */
    runToRest(listener: SimListener = {}, maxTime = 600) {
        const t0 = this.now;
        while (!this.allStopped() && this.now - t0 < maxTime) {
            if (this.next === undefined) this.next = this.findNext();
            const ev = this.next;
            const target = ev ? ev.time : this.now + maxTime;
            this.advanceTo(Math.min(Math.max(target, this.now), t0 + maxTime), listener);
        }
        if (this.next === undefined) this.next = this.findNext();
        // stationary balls left inside a pocket are potted too (at the same instant)
        this.advanceTo(this.now, listener);
        return this.now - t0;
    }

    // ----- internals -----

    private sAt(tau: number) {
        const k = this.params.damping;
        return -Math.expm1(-k * tau) / k;
    }

    private tauOf(s: number) {
        const k = this.params.damping;
        if (s <= 0) return 0;
        return -Math.log1p(-k * s) / k;
    }

    /** Commit the state at the current time (before an external change). */
    private commit() {
        if (this.now > this.baseTime) this.rebase(this.sAt(this.now - this.baseTime), this.now);
    }

    private rebase(s: number, time: number) {
        if (s > 0) {
            const f = 1 - this.params.damping * s;    // = e^(-k tau)
            for (const b of this.balls) {
                if (!b.onTable || (b.vx === 0 && b.vy === 0)) continue;
                b.x += b.vx * s; b.y += b.vy * s;
                b.vx *= f; b.vy *= f;
            }
        }
        this.baseTime = time;
        if (this.now < time) this.now = time;
    }

    private stopS(b: SimBall) {
        const speed = Math.hypot(b.vx, b.vy);
        if (speed <= this.params.stopSpeed) return 0;
        return (1 - this.params.stopSpeed / speed) / this.params.damping;
    }

    private touch(i: number) {
        this.dirty.add(i);
        this.next = undefined;
    }

    private toTime(s: number) {
        return s === Infinity ? Infinity : this.baseTime + this.tauOf(s);
    }

    /** Ball i's own next event: stop, cushion segment, vertex or pocket (absolute time). */
    private ballEvent(i: number) {
        const b = this.balls[i];
        const R = this.params.radius;
        let best = Infinity, kind: EventKind = "stop", j = -1;
        const take = (s: number, k: EventKind, jj: number) => { if (s < best) { best = s; kind = k; j = jj; } };
        if (!b.onTable) { this.ballT[i] = Infinity; return; }
        if (b.vx === 0 && b.vy === 0) {
            // a ball at rest inside a pocket (put there) falls in, as it always did
            for (let p = 0; p < this.boundary.pockets.length; p++) if (this.pocketEntry(b, p, 0) === 0) take(0, "pocket", p);
        } else {
            take(this.stopS(b), "stop", -1);
            for (let k = 0; k < this.segData.length; k++) take(this.segmentTime(b, k, best), "segment", k);
            const vs = this.boundary.vertices;
            for (let k = 0; k < vs.length; k++) take(contactTime(b.x - vs[k][0], b.y - vs[k][1], b.vx, b.vy, R, best), "vertex", k);
            for (let p = 0; p < this.boundary.pockets.length; p++) take(this.pocketEntry(b, p, best), "pocket", p);
        }
        this.ballT[i] = this.toTime(best);
        this.ballKind[i] = kind;
        this.ballJ[i] = j;
    }

    private pairEvent(i: number, j: number) {
        const bi = this.balls[i], bj = this.balls[j];
        let s = Infinity;
        const movingI = bi.vx !== 0 || bi.vy !== 0, movingJ = bj.vx !== 0 || bj.vy !== 0;
        if (bi.onTable && bj.onTable && (movingI || movingJ)) {
            const limit = Math.min(movingI ? this.stopS(bi) : Infinity, movingJ ? this.stopS(bj) : Infinity);
            s = contactTime(bj.x - bi.x, bj.y - bi.y, bj.vx - bi.vx, bj.vy - bi.vy, 2 * this.params.radius, limit);
        }
        this.pairT[i][j] = this.toTime(s);
    }

    private findNext(): NextEvent | null {
        const n = this.balls.length;
        if (this.allDirty || this.pairT.length !== n) {
            this.pairT = Array.from({length: n}, () => new Array(n).fill(Infinity));
            this.ballT = new Array(n).fill(Infinity);
            for (let i = 0; i < n; i++) {
                this.ballEvent(i);
                for (let j = i + 1; j < n; j++) this.pairEvent(i, j);
            }
        } else {
            this.dirty.forEach(i => {
                this.ballEvent(i);
                for (let j = 0; j < n; j++) if (j !== i) this.pairEvent(Math.min(i, j), Math.max(i, j));
            });
        }
        this.allDirty = false;
        this.dirty.clear();
        let best: NextEvent | null = null;
        for (let i = 0; i < n; i++) {
            if (this.ballT[i] < Infinity && (!best || this.ballT[i] < best.time)) best = {time: this.ballT[i], kind: this.ballKind[i], i, j: this.ballJ[i]};
            const row = this.pairT[i];
            for (let j = i + 1; j < n; j++) if (row[j] < Infinity && (!best || row[j] < best.time)) best = {time: row[j], kind: "ball", i, j};
        }
        return best;
    }

    private segmentTime(b: SimBall, k: number, limit: number) {
        const g = this.segData[k];
        const R = this.params.radius;
        let d = (b.x - g.ax) * g.nx + (b.y - g.ay) * g.ny;
        let vn = b.vx * g.nx + b.vy * g.ny;
        if (d < 0) { d = -d; vn = -vn; }           // measure from the side the ball is on
        if (!(vn < -APPROACH_EPS)) return Infinity;
        const s = d <= R ? 0 : (d - R) / -vn;
        if (s > limit) return Infinity;
        const u = (b.x + b.vx * s - g.ax) * g.tx + (b.y + b.vy * s - g.ay) * g.ty;
        if (u < 0 || u > g.len) return Infinity;     // past an end: the vertex test covers it
        return s;
    }

    /** First s in [0, limit] at which the centre is inside pocket p (Cyrus-Beck clipping), or Infinity. */
    private pocketEntry(b: SimBall, p: number, limit: number) {
        const poly = this.boundary.pockets[p];
        let lo = 0, hi = limit;
        for (let e = 0, m = poly.length; e < m; e++) {
            const p1 = poly[e], p2 = poly[(e + 1) % m];
            // inside this edge: cross(p1 - c, p2 - c) <= 0, with c = (x, y) + v s: f0 + g s <= 0
            const ax = p1[0] - b.x, ay = p1[1] - b.y, bx = p2[0] - b.x, by = p2[1] - b.y;
            const f0 = ax * by - ay * bx;
            const g = -(b.vx * (p2[1] - p1[1]) - b.vy * (p2[0] - p1[0]));
            if (g === 0) { if (f0 > 0) return Infinity; continue; }
            const r = -f0 / g;
            if (g > 0) { if (r < hi) hi = r; } else if (r > lo) lo = r;
            if (lo > hi) return Infinity;
        }
        return lo;
    }

    private resolve(ev: NextEvent, listener: SimListener) {
        const b = this.balls[ev.i];
        const R = this.params.radius;
        switch (ev.kind) {
            case "stop":
                b.vx = 0; b.vy = 0;
                if (listener.onStopped) listener.onStopped(b.id);
                return;
            case "pocket":
                b.vx = 0; b.vy = 0; b.onTable = false;
                if (listener.onPotted) listener.onPotted(b.id, [b.x, b.y]);
                return;
            case "ball": {
                const o = this.balls[ev.j];
                let nx = o.x - b.x, ny = o.y - b.y;
                const d = Math.hypot(nx, ny) || 1;
                nx /= d; ny /= d;
                const vrel = (b.vx - o.vx) * nx + (b.vy - o.vy) * ny;   // > 0: approaching
                if (vrel <= 0) return;
                const e = vrel < MIN_BOUNCE_SPEED ? 0 : this.params.ballRestitution;
                const jn = (1 + e) / 2 * vrel;                           // equal masses
                b.vx -= jn * nx; b.vy -= jn * ny;
                o.vx += jn * nx; o.vy += jn * ny;
                // nudge apart if rounding left them overlapping (never let an overlap persist)
                const gap = 2 * R - d;
                if (gap > 0) {
                    b.x -= nx * gap / 2; b.y -= ny * gap / 2;
                    o.x += nx * gap / 2; o.y += ny * gap / 2;
                }
                if (listener.onBallContact) listener.onBallContact(b.id, o.id, vrel);
                return;
            }
            case "segment":
            case "vertex": {
                let nx: number, ny: number, gap: number;
                if (ev.kind === "segment") {
                    const g = this.segData[ev.j];
                    const d = (b.x - g.ax) * g.nx + (b.y - g.ay) * g.ny;
                    const sign = d < 0 ? -1 : 1;
                    nx = g.nx * sign; ny = g.ny * sign;
                    gap = R - Math.abs(d);
                } else {
                    const v = this.boundary.vertices[ev.j];
                    nx = b.x - v[0]; ny = b.y - v[1];
                    const d = Math.hypot(nx, ny) || 1;
                    nx /= d; ny /= d;
                    gap = R - d;
                }
                const vn = b.vx * nx + b.vy * ny;                        // < 0: into the cushion
                if (vn >= 0) return;
                const e = -vn < MIN_BOUNCE_SPEED ? 0 : this.params.cushionRestitution;
                b.vx -= (1 + e) * vn * nx; b.vy -= (1 + e) * vn * ny;
                if (gap > 0) { b.x += nx * gap; b.y += ny * gap; }
                if (listener.onCushion) listener.onCushion(b.id, -vn);
                return;
            }
        }
    }
}
